import React from 'react'

import { Formik } from 'formik'

import { ZariContext } from '_zari/context'
import { AdminContext } from 'contexts/AdminContext'

import { Button, Col, Divider, Form, Row } from '_zari/core'
import Yup from '_zari/validation/_yup'

import {
  TiposDocumentoSunatConstant,
  OrigenesContabilidadConstant,
  CondicionesPagoConstant,
  TiposGuardadoGlosaConstant,
  MonedasConstant
} from 'constants/index'

import { IdGen, DateHandler, ZrSwal } from 'utils'

import * as CajaChicaService from 'services/cajaChica.service'
import * as CompaniaService from 'services/compania.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'
import * as Modals from './modals'

export const ValidationSchema = Yup.object().shape({
  fechaDocumento: Yup.string().required(),
  _idSocio: Yup.string().required(),
  glosa: Yup.string().required(),
  tipoDocumento: Yup.string()
    .required()
    .when('origen', {
      is: OrigenesContabilidadConstant.VENTAS,
      then: Yup.string().oneOf(TiposDocumentoSunatConstant.__values)
    }),
  tipoCambio: Yup.number().min(1).required(),
  serieDocumento: Yup.string()
    .required()
    .when('origen', {
      is: OrigenesContabilidadConstant.VENTAS,
      then: Yup.string().matches(/^[0FBfbEe]/, 'La serie debe iniciar con E, F, B o 0(cero).'),
      otherwise: Yup.string().when('tipoDocumento', {
        is: '02',
        then: Yup.string().matches(/^[Ee]/, 'La serie debe iniciar con E.')
      })
    }),
  numeroDocumento: Yup.string().required()
})

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      return [
        ...state,
        {
          ...action.payload,
          _idFila: IdGen.newId()
        }
      ]
    }
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

const MainForm = ({ edicionInfo, idTituloViatico, idSocio, mostrarListaSection }) => {
  const __zariContext = React.useContext(ZariContext)
  const __adminContext = React.useContext(AdminContext)

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, edicionInfo.detalles, innerDetails => {
    if (innerDetails) {
      return innerDetails.map(x => ({
        ...x,
        _idFila: IdGen.newId()
      }))
    } else {
      return []
    }
  })

  const mainFormikRef = React.useRef(null)
  const cabeceraDetalleFormikRef = React.useRef(null)
  const montosFormRef = React.useRef(null)
  const mainFormDataRef = React.useRef({})
  const tieneRetencionRhRef = React.useRef(false)
  const tienePercepcionRef = React.useRef(false)
  const condicionPagoRef = React.useRef('')
  const condicionPagoHistorialRef = React.useRef('')

  const [mostrarInfoNotaCredDebModal, setMostrarInfoNotaCredDebModal] = React.useState(false)

  const [mostrarCuenta1242Temporal, setMostrarCuenta1242Temporal] = React.useState(false)
  const [soloDetalleVacio, setSoloDetalleVacio] = React.useState(false)
  const [cuentasRetencionPercepcion, setCuentasRetencionPercepcion] = React.useState({
    cuentaContablePercepcionIGV: '',
    cuentaContableRetencionRH: '',
    infoCuentaContablePercepcionIGV: '',
    infoCuentaContableRetencionRH: ''
  })

  React.useEffect(() => {
    CompaniaService.getCuentasRetencionPercepcion().then(({ data }) => {
      setCuentasRetencionPercepcion(data)
    })
  }, [])

  const formInitialValues = React.useMemo(() => {
    return {
      _id: edicionInfo._id || '',
      _idGlosa: edicionInfo.glosa || '',
      _idSocio: edicionInfo._idSocio || '',
      _idTituloViatico: idTituloViatico,
      _idTrabajadorViatico: idSocio,
      _idPlantilla: edicionInfo._idPlantilla || '',
      _idPartida: edicionInfo._idPartida || '',
      _idTrabajador: edicionInfo._idTrabajador || '',
      anioAduana: edicionInfo.anioAduana || '',
      codigoSocio: edicionInfo.codigoSocio || '',
      codigoAduana: edicionInfo.codigoAduana || '',
      cuenta1242Temporal: '',
      cuenta1242TemporalInfo: '',
      documentoSocio: edicionInfo.documentoSocio || '',
      documentoTrabajador: edicionInfo.documentoTrabajador || '',
      fechaDocumento: edicionInfo.fechaEmision || '',
      fechaVencimiento: edicionInfo.fechaVencimiento || '',
      glosa: edicionInfo.glosa || '',
      glosaComplemento: edicionInfo.glosaComplemento || '',
      moneda: edicionInfo.moneda || '',
      nombreSocio: edicionInfo.nombreSocio || '',
      nombreTrabajador: edicionInfo.nombreTrabajador || '',
      origen: OrigenesContabilidadConstant.COMPRAS,
      tipoCambio: edicionInfo.tipoCambio || 1,
      tipoDocumento: edicionInfo.tipoDocumento || '01',
      tipoDocumentoSocio: edicionInfo.tipoDocumentoSocio || '',
      tipoRetencion: edicionInfo.tipoRetencion || '',
      tipoGuardadoGlosa: edicionInfo.tipoGuardadoGlosa || TiposGuardadoGlosaConstant.PERSONALIZADO,
      serieDocumento: edicionInfo.serieDocumento || '',
      numeroDocumento: edicionInfo.numeroDocumento || '',
      numeroDocumentoFin: edicionInfo.numeroDocumentoFin || '',
      valor1500Uit: edicionInfo.valor1500Uit || ''
    }
  }, [idTituloViatico, idSocio, edicionInfo])

  const submitFormularioPagos = React.useCallback(
    (formData, { setSubmitting } = {}) => {
      CajaChicaService.nuevoConDocumento(formData)
        .then(() => {
          mostrarListaSection()
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          if (setSubmitting) {
            setSubmitting(false)
          }
        })
    },
    [__zariContext, mostrarListaSection]
  )

  const submitInformacionNotaCredDeb = React.useCallback(
    formData => {
      formData.pago = {
        condicionPago: CondicionesPagoConstant.CREDITO
      }

      mainFormDataRef.current = formData

      CajaChicaService.nuevoConDocumento(formData)
        .then(({ data }) => {
          mostrarListaSection()
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
        })
    },
    [__zariContext, mostrarListaSection]
  )

  const onSubmit = React.useCallback(
    (formData, { setSubmitting }) => {
      tieneRetencionRhRef.current = false
      tienePercepcionRef.current = false

      if (!soloDetalleVacio) {
        if (detalles.length === 0) {
          ZrSwal.showAlert({
            tipo: 'error',
            mensaje: 'Ingresar detalles.',
            onSuccess: () => setSubmitting(false)
          })
          return
        }
      }

      if (formData.origen === OrigenesContabilidadConstant.COMPRAS && __adminContext.flag1500Uit) {
        if (!formData.valor1500Uit) {
          ZrSwal.showAlert({
            tipo: 'error',
            mensaje: 'El valor 1500 UIT es obligatorio.',
            onSuccess: () => setSubmitting(false)
          })
          return
        }
      }

      const tieneCuenta636 = detalles.some(x => x.cuentaContable.startsWith('636'))
      const tieneRetencionRh = detalles.some(
        x => x.cuentaContable === cuentasRetencionPercepcion.cuentaContableRetencionRH
      )
      const tienePercepcion = detalles.some(
        x => x.cuentaContable === cuentasRetencionPercepcion.cuentaContablePercepcionIGV
      )

      tieneRetencionRhRef.current = tieneRetencionRh
      tienePercepcionRef.current = tienePercepcion

      let numeroModificaciones = 0
      if (tieneRetencionRh) numeroModificaciones++
      if (tienePercepcion) numeroModificaciones++

      if (numeroModificaciones > 1) {
        ZrSwal.showAlert({
          tipo: 'error',
          mensaje: 'El asiento no puede tener Retencion y/o Percepcion y/o Fondo Fijo al mismo tiempo.',
          onSuccess: () => setSubmitting(false)
        })
        return
      }

      if (tieneCuenta636 && formData.tipoDocumento !== '14') {
        ZrSwal.showAlert({
          tipo: 'error',
          mensaje: 'La cuenta "636" solo es admitida con tipo de documento "14".',
          onSuccess: () => setSubmitting(false)
        })
        return
      }

      const submitData = {
        ...formData,
        tieneRetencion: tieneRetencionRh,
        numeroDocumento: parseFloat(formData.numeroDocumento),
        detalles: detalles,
        importeIgv: parseFloat(montosFormRef.current.values.importeIgv),
        importeTotal: parseFloat(montosFormRef.current.values.importeTotal)
      }
      mainFormDataRef.current = submitData

      const parsedDate = DateHandler.parse(submitData.fechaDocumento)
      const mesSeleccionado = parsedDate.getMonth() + 1
      const anioSeleccionado = parsedDate.getFullYear()
      const mesContabilidad = parseFloat(__adminContext.mesTrabajo)
      const anioContabilidad = parseFloat(__adminContext.anioTrabajo)

      const fechaMayorAContabilidad = mesSeleccionado > mesContabilidad && anioSeleccionado >= anioContabilidad

      if (fechaMayorAContabilidad) {
        ZrSwal.showAlert({
          tipo: 'error',
          mensaje: 'Fecha de documento superior al mes de contabilidad.',
          onSuccess: () => setSubmitting(false)
        })
        return
      }

      if (submitData.tipoDocumento === '14') {
        const parsedVencimintoDate = DateHandler.parse(submitData.fechaVencimiento)
        const mesVencimientoSeleccionado = parsedVencimintoDate.getMonth() + 1
        const anioVencimientoSeleccionado = parsedVencimintoDate.getFullYear()

        const validacionContabilidadDoc14 =
          mesVencimientoSeleccionado > mesContabilidad + 1 && anioVencimientoSeleccionado >= anioContabilidad

        if (validacionContabilidadDoc14) {
          ZrSwal.showAlert({
            tipo: 'error',
            mensaje: 'Fecha de vencimiento superior al mes siguiente de contabilidad.',
            onSuccess: () => setSubmitting(false)
          })
          return
        }
      }

      const matchMes = mesSeleccionado === mesContabilidad
      const matchAnio = anioSeleccionado === anioContabilidad

      const documentosNotas = ['07', '08', '87', '88', '97', '98']
      if (documentosNotas.some(x => x === submitData.tipoDocumento)) {
        setMostrarInfoNotaCredDebModal(true)
      } else {
        if (soloDetalleVacio) {
          condicionPagoRef.current = CondicionesPagoConstant.CREDITO
        } else {
          if (
            (submitData.importeTotal <= 3500 && submitData.moneda === MonedasConstant.SOLES) ||
            (submitData.importeTotal <= 1000 && submitData.moneda === MonedasConstant.DOLARES)
          ) {
            condicionPagoRef.current = CondicionesPagoConstant.CONTADO
          } else {
            condicionPagoRef.current = CondicionesPagoConstant.CREDITO
          }
        }

        if (matchMes && matchAnio) {
          submitFormularioPagos(mainFormDataRef.current)
        } else {
          if (submitData.origen === OrigenesContabilidadConstant.COMPRAS) {
            ZrSwal.showAlert({
              tipo: 'info',
              mensaje: 'Mes contabilidad es distinto al mes de fecha documento. ¿Desea continuar?',
              showCerrar: true,
              onSuccess: () => submitFormularioPagos(mainFormDataRef.current),
              onClose: () => setSubmitting(false)
            })
          } else {
            ZrSwal.showAlert({
              tipo: 'error',
              mensaje: 'Mes contabilidad es distinto al mes de fecha documento.',
              onSuccess: () => setSubmitting(false)
            })
          }
        }
      }
    },
    [__adminContext, detalles, submitFormularioPagos, cuentasRetencionPercepcion, soloDetalleVacio]
  )

  const clickSubmit = React.useCallback(() => {
    mainFormikRef.current.handleSubmit()
  }, [])

  const changeMostrarCuenta1242Temporal = React.useCallback(() => {
    setMostrarCuenta1242Temporal(prev => {
      if (prev) {
        mainFormikRef.current.setValues({
          ...mainFormikRef.current.values,
          cuenta1242Temporal: '',
          cuenta1242TemporalInfo: ''
        })
      } else {
        mainFormikRef.current.setValues({
          ...mainFormikRef.current.values,
          _idPlantilla: ''
        })
      }

      return !prev
    })
  }, [])

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        innerRef={mainFormikRef}
        onSubmit={onSubmit}
        validationSchema={ValidationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        validateOnMount={false}
      >
        {({ errors, handleChange, handleBlur, isSubmitting, setFieldValue, setValues, touched, values }) => (
          <Form>
            <Row>
              <Col md={5}>
                <Row>
                  <Col md={6}>
                    <FormGroups.FechaDocumentoFormGroup
                      vId={values._id}
                      iError={errors.fechaDocumento}
                      iTouched={touched.fechaDocumento}
                      fIsSubmitting={isSubmitting}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      vSocio={values._idSocio}
                      values={values}
                    />
                  </Col>
                  <Col md={6}>
                    <FormGroups.FechaVencimientoFormGroup
                      iError={errors.fechaVencimiento}
                      iTouched={touched.fechaVencimiento}
                      fIsSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <FormGroups.SocioFormGroup
                  iTouched
                  iError={errors._idSocio}
                  fIsSubmitting={isSubmitting}
                  iValue={values._idSocio}
                  values={values}
                  setValues={setValues}
                  vOrigen={values.origen}
                  condicionPagoRef={condicionPagoHistorialRef}
                />
                <FormGroups.GlosaFormGroup
                  iTouched={touched.glosa}
                  vSocio={values._idSocio}
                  fIsSubmitting={isSubmitting}
                  iError={errors.glosa}
                  setFieldValue={setFieldValue}
                  cabeceraDetalleFormikRef={cabeceraDetalleFormikRef}
                  vOrigen={values.origen}
                  vTipoGuardadoGlosa={values.tipoGuardadoGlosa}
                  prevDataGlosa=''
                />
                <FormGroups.TipoDocumentoFormGroup
                  iError={errors.tipoDocumento}
                  iTouched={touched.tipoDocumento}
                  fIsSubmitting={isSubmitting}
                  iOrigen={values.origen}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  cabeceraDetalleFormikRef={cabeceraDetalleFormikRef}
                />
                <Row>
                  <Col md={6}>
                    {values.tipoDocumento === '50' || values.tipoDocumento === '52' ? (
                      <FormGroups.SerieAduanaFormGroup
                        iError={errors.serieDocumento}
                        iTouched={touched.serieDocumento}
                        fIsSubmitting={isSubmitting}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                      />
                    ) : (
                      <FormGroups.SerieDocumentoFormGroup
                        iError={errors.serieDocumento}
                        iTouched={touched.serieDocumento}
                        fIsSubmitting={isSubmitting}
                        setFieldValue={setFieldValue}
                      />
                    )}
                  </Col>
                  <Col md={6}>
                    <FormGroups.NumeroDocumentoFormGroup
                      iError={errors.numeroDocumento}
                      iTouched={touched.numeroDocumento}
                      fIsSubmitting={isSubmitting}
                      vTipoDocumento={values.tipoDocumento}
                      vMoneda={values.moneda}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4}>
                <Row>
                  <Col md={6}>
                    <FormGroups.MonedaFormGroup
                      iOrigen={values.origen}
                      iError={errors.moneda}
                      iTouched={touched.moneda}
                      fIsSubmitting={isSubmitting}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      montosFormRef={montosFormRef}
                    />
                  </Col>
                  <Col md={6}>
                    <FormGroups.TipoCambioFormGroup
                      iError={errors.tipoCambio}
                      iTouched={touched.tipoCambio}
                      fIsSubmitting={isSubmitting}
                      iMoneda={values.moneda}
                      iFechaDocumento={values.fechaDocumento}
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Checkbox
                      id='checkbox-mostrarcuenta1242'
                      name='checkbox-mostrarcuenta1242'
                      label='Usar cuenta temporal'
                      onChange={changeMostrarCuenta1242Temporal}
                      checked={mostrarCuenta1242Temporal}
                    />
                  </Col>
                </Row>
                {mostrarCuenta1242Temporal ? (
                  <FormGroups.Cuenta1242TemporalFormGroup
                    iError={errors.cuenta1242Temporal}
                    iTouched
                    iValue={values.cuenta1242TemporalInfo}
                    setFieldValue={setFieldValue}
                  />
                ) : (
                  <FormGroups.PlantillaFormGroup
                    iError={errors._idPlantilla}
                    iTouched={touched._idPlantilla}
                    fIsSubmitting={isSubmitting}
                    iOrigen={values.origen}
                    vTipoDocumento={values.tipoDocumento}
                    setFieldValue={setFieldValue}
                  />
                )}
                {values.tipoDocumento === '50' || values.tipoDocumento === '52' ? (
                  <FormGroups.AnioAduanaFormGroup
                    iError={errors.anioAduana}
                    iTouched={touched.anioAduana}
                    fIsSubmitting={isSubmitting}
                  />
                ) : null}
                {values.tipoDocumento === '03' && (
                  <FormGroups.NumeroDocumentoFinFormGroup
                    iError={errors.numeroDocumentoFin}
                    iTouched={touched.numeroDocumentoFin}
                    fIsSubmitting={isSubmitting}
                  />
                )}
                <FormGroups.TipoRetencionFormGroup
                  iValue={values.tipoRetencion}
                  dispatch={detallesDispatcher}
                  cuentasRetencionPercepcion={cuentasRetencionPercepcion}
                  handleChange={handleChange}
                  vTipoDocumento={values.tipoDocumento}
                />
                <Row>
                  <Col md={12}>
                    <Form.Checkbox
                      id='checkbox-detalleVacio'
                      name='checkbox-detalleVacio'
                      label='Valor cero'
                      onChange={() => setSoloDetalleVacio(prev => !prev)}
                      checked={soloDetalleVacio}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={3}>
                {values.origen === OrigenesContabilidadConstant.COMPRAS && (
                  <>
                    {__adminContext.flag1500Uit && (
                      <FormGroups.Valor1500UitFormGroup
                        iError={errors.valor1500Uit}
                        iTouched={touched.valor1500Uit}
                        fIsSubmitting={isSubmitting}
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      <Divider noMargin />
      <Sections.DetallesCabeceraSection
        innerFormikRef={cabeceraDetalleFormikRef}
        iOrigen={formInitialValues.origen}
        dispatch={detallesDispatcher}
        mainFormikRef={mainFormikRef}
      />
      <Divider noMargin />
      <Sections.DetallesTablaSection
        iOrigen={formInitialValues.origen}
        detalles={detalles}
        dispatch={detallesDispatcher}
      />
      <Divider noMargin />
      <Sections.ResumenMontosSection
        innerRef={montosFormRef}
        detalles={detalles}
        iOrigen={formInitialValues.origen}
        defMoneda={formInitialValues.moneda}
      />
      <Divider noMargin />
      <Row>
        <Col md={12} className='text-right'>
          <Button color='secondary' onClick={mostrarListaSection}>
            Cancelar
          </Button>
          <Button onClick={clickSubmit}>Procesar</Button>
        </Col>
      </Row>
      {mostrarInfoNotaCredDebModal && (
        <Modals.InformacionNotaCredDebModal
          show={mostrarInfoNotaCredDebModal}
          handleClose={() => {
            setMostrarInfoNotaCredDebModal(false)
            mainFormikRef.current.setSubmitting(false)
          }}
          mainFormData={mainFormDataRef.current}
          submitFunction={submitInformacionNotaCredDeb}
        />
      )}
    </>
  )
}

export default MainForm
